import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useFormBuilderStore } from '@/stores/formBuilderStore'
import type { Question } from '@/types/form'
import { AppearancePanel } from './AppearancePanel'
import { LogicPanel } from './LogicPanel'
import { QuestionSettingsFields } from './QuestionSettingsFields'
import { CustomerFieldBindingSettings } from './Settings/CustomerFieldBindingSettings'

export function SettingsPanel() {
  const form = useFormBuilderStore((state) => state.form)
  const questions = useFormBuilderStore((state) => state.questions)
  const questionGroups = useFormBuilderStore((state) => state.questionGroups)
  const selectedQuestionId = useFormBuilderStore((state) => state.selectedQuestionId)
  const activeTab = useFormBuilderStore((state) => state.activeTab)
  const setActiveTab = useFormBuilderStore((state) => state.setActiveTab)
  const updateQuestion = useFormBuilderStore((state) => state.updateQuestion)
  const updateForm = useFormBuilderStore((state) => state.updateForm)
  const question = questions.find((item) => item.id === selectedQuestionId)
  const selectedQuestionGroup = questionGroups.find((group) =>
    question ? group.questionIds.includes(question.id) : false,
  )
  const isReadonlyQuestion = selectedQuestionGroup?.mode === 'synced'

  function updateSettings(patch: Partial<Question['settings']>) {
    if (!question || isReadonlyQuestion) return
    updateQuestion(question.id, { settings: { ...question.settings, ...patch } })
  }

  function updateSelectedQuestion(patch: Partial<Question>) {
    if (!question || isReadonlyQuestion) return
    updateQuestion(question.id, patch)
  }

  return (
    <aside className="flex h-full min-h-0 w-full flex-col overflow-hidden border-l border-slate-200 bg-white">
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as typeof activeTab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="shrink-0 border-b border-slate-200 p-3">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="question">問題</TabsTrigger>
            <TabsTrigger value="appearance">外觀</TabsTrigger>
            <TabsTrigger value="logic">邏輯</TabsTrigger>
            <TabsTrigger value="binding">綁定</TabsTrigger>
          </TabsList>
        </div>
        <ScrollArea className="min-h-0 flex-1" viewportClassName="p-4">
          <TabsContent value="question" className="mt-0">
            {question ? (
              <QuestionSettingsFields
                question={question}
                questions={questions}
                disabled={isReadonlyQuestion}
                readonlyNotice={
                  isReadonlyQuestion ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-900">
                      <div className="mb-1 flex items-center gap-2 font-medium">
                        <Badge variant="warning">同步題組唯讀</Badge>
                        {selectedQuestionGroup?.title}
                      </div>
                      這題來自同步題組，問卷內只能預覽與設定題組排序；題目內容需回到題組管理修改。
                    </div>
                  ) : undefined
                }
                onQuestionChange={updateSelectedQuestion}
                onSettingsChange={updateSettings}
                showButtonTextSetting={form.settings.fillMode === 'step'}
              />
            ) : (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Label>問卷標題</Label>
                  <Input
                    value={form.title}
                    onChange={(event) => updateForm({ title: event.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label>問卷描述</Label>
                  <Textarea
                    value={form.description}
                    onChange={(event) => updateForm({ description: event.target.value })}
                  />
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="appearance" className="mt-0">
            <AppearancePanel />
          </TabsContent>
          <TabsContent value="logic" className="mt-0">
            <LogicPanel />
          </TabsContent>
          <TabsContent value="binding" className="mt-0">
            {question ? (
              <CustomerFieldBindingSettings question={question} disabled={isReadonlyQuestion} />
            ) : (
              <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm leading-6 text-slate-500">
                請先選擇左欄題目，再設定新客申請欄位綁定。版面區塊不會產生可帶入申請表的答案。
              </div>
            )}
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </aside>
  )
}
